import React, { createContext, useCallback, useEffect } from 'react';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';

import {
  getAiSuggestComponent, setDestinationLecturePage,
  setAiSuggestComponentsLoaded, setAiSuggestComponentsLoading,
} from 'redux/actions/lecture-pages';
import { LecturePageMode } from 'lecture_pages/components';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

import { useSelector } from 'react-redux';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getSuggestedActivities } from 'redux/selectors/lecture-page';

import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import { Button } from 'react-bootstrap';
import { aiSuggestingComponentsParamKeys } from 'redux/schemas/models/ai-course-creation';
import SuggestedActivities from './suggested-activities';
import AiGeneratingLoader from '../shared/ai-generating-loader';
import { config } from '@config/config.json';

export type ActivitySuggestionContextType = {
  openAddToLessons: () => void;
};

export const ActivitySuggestionContext = createContext<ActivitySuggestionContextType | null>(null);

type ModalContentProps = {
  closeModal: () => void,
  onAddLesson: () => void,
};

type AbortablePromise = Promise<void> & { abort?: () => void };

const SuggestActivitiesModalContent = (props: ModalContentProps) => {
  const { onAddLesson, closeModal } = props;

  const params = useLecturePageParams();
  const dispatch = useAppDispatch();
  const [successRequestCount, setSuccessRequestCount] = React.useState(0);
  const [failedRequestCount, setFailedRequestCount] = React.useState(0);
  const isMountedRef = React.useRef(true);
  const aiSuggestRequestsRef = React.useRef<AbortablePromise[]>([]);

  const activities = useSelector(getSuggestedActivities);
  const { isLoaded, isLoading } = useSelector(state => state.app.aiSuggestComponent);

  const openAddToLessons = () => {
    dispatch(setDestinationLecturePage({
      mode: LecturePageMode.VIEW,
      lecturePageId: params.lecturePageId,
      catalogId: params.catalogId,
    }));
    onAddLesson();
  };

  useEffect(() => () => {
    // Mark component as unmounted to avoid state updates
    isMountedRef.current = false;

    // Abort any active AI suggest requests
    aiSuggestRequestsRef.current.forEach((request) => {
      if (request && typeof request.abort === 'function') {
        request.abort();
      }
    });
    aiSuggestRequestsRef.current = [];
  }, []);

  useEffect(() => {
    // Show alert if no activities were suggested
    if (isLoaded) {
      if (activities.length === 0) {
        closeModal();
        hideAddUI(dispatch);

        // All API calls succeeded but no activities were suggested
        if (aiSuggestingComponentsParamKeys.length === successRequestCount) {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.REQUEST_ALERT.NO_ACTIVITIES.FULL_SUCCESS.TITLE(),
            message: t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.REQUEST_ALERT.NO_ACTIVITIES.FULL_SUCCESS.CONTENT(),
          }));
        // Some API calls failed
        } else if (failedRequestCount > 0) {
          dispatch(addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message: successRequestCount > 0
              // Some apis succeeded and some failed but no activities were suggested
              ? t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.REQUEST_ALERT.NO_ACTIVITIES.PARTIAL_ERROR()
              : t.FORM.ERROR_SOMETHING_WRONG_SHORT(),
          }));
        }
      } else if (failedRequestCount > 0) {
        // Certain API calls fail and others succeed with some activities suggested
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          message: t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.REQUEST_ALERT.PARTIAL_GENERATION(),
        }));
      }
    }
  }, [isLoaded]);

  const getActivities = useCallback(() => {
    dispatch(setAiSuggestComponentsLoading(true));

    aiSuggestRequestsRef.current = [];

    const requests = aiSuggestingComponentsParamKeys.map((activityType) => {
      const thunkPromise = dispatch(getAiSuggestComponent({
        catalogId: params.catalogId,
        lecturePageId: params.lecturePageId,
        activityType,
      }));

      aiSuggestRequestsRef.current.push(thunkPromise);

      return wrapThunkAction(
        thunkPromise,
      ).then(() => {
        if (isMountedRef.current) {
          setSuccessRequestCount((count) => count + 1);
        }
      }).catch(() => {
        if (isMountedRef.current) {
          setFailedRequestCount((count) => count + 1);
        }
      });
    });

    Promise.all(requests).finally(() => {
      if (isMountedRef.current) {
        dispatch(setAiSuggestComponentsLoaded(true));
        dispatch(setAiSuggestComponentsLoading(false));
      }
      // activities here may still be stale, so using a useEffect watching
      // activities and isLoading to show completion alert
    });
  }, []);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      getActivities();
    }
  }, [getActivities, isLoaded, isLoading]);

  const suggestionContextValue: ActivitySuggestionContextType = {
    openAddToLessons,
  };

  return (
    <div>
      {!isLoaded && activities.length === 0 ? (
        <AiGeneratingLoader
          customDescription={t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.LOADING_DESCRIPTION()}
        />
      ) : (
        <div className='d-flex flex-column align-items-center justify-content-center px-8 py-5'>
          {activities.length > 0 && (
            <ActivitySuggestionContext.Provider value={suggestionContextValue}>
              <SuggestedActivities />
              <Button
                size='lg'
                variant='primary'
                className='mt-6 mb-2'
                onClick={closeModal}
                data-qa={config.pendo.novoAi.suggestActivities.closeModal}
              >
                {t.FORM.CLOSE()}
              </Button>
            </ActivitySuggestionContext.Provider>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestActivitiesModalContent;
